import { HttpErrorResponse } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';

import { AppComponent } from './app/app';
import { createAppProviders } from './app/app.config';
import { environment } from './environments/environment';

const GOOGLE_MAPS_PLACES_LOAD_ERROR = 'Could not load "places"';
const GOOGLE_MAPS_ZERO_RESULTS_STATUS = 'ZERO_RESULTS';
const GOOGLE_MAPS_TRUSTED_TYPES_CALLBACK_ERROR = 'The provided callback is no longer runnable';
const THIRD_PARTY_SCRIPT_PARSE_ERROR = 'No identifiers allowed directly after numeric literal';

const BROWSER_EXTENSION_URL_PATTERN = /^(chrome|moz|safari(-web)?|ms-browser)-extension:\/\//;
// Network-level fetch failures (offline, aborted navigation, blocked request) per browser: Chrome, Safari, Firefox.
const NETWORK_FETCH_FAILURE_PATTERN =
  /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)( \(.*\))?$/;

function shouldDropSentryEvent(event: any, hint: any): boolean {
  return (
    getSentryEventMessages(event, hint).some(isIgnoredSentryMessage) ||
    hasBrowserExtensionFrame(event) ||
    isNetworkFetchFailure(event) ||
    hasOnlyAnonymousFrames(event) ||
    hasZoneJsFrame(event)
  );
}

// HttpClient errors from subscriptions without an error callback are rethrown by RxJS via setTimeout,
// so they bypass Angular's ErrorHandler and reach Sentry's window.onerror handler as a raw
// HttpErrorResponse ("Object captured as exception with keys: error, headers, ..."). Apply the same
// rules as ErrorInterceptor: drop status 0 / 4xx, and give the rest a readable message.
function isHttpErrorResponse(value: any): value is HttpErrorResponse {
  return value instanceof HttpErrorResponse || value?.name === 'HttpErrorResponse';
}

function shouldReportHttpError(err: HttpErrorResponse): boolean {
  return err.status !== 0 && !(err.status >= 400 && err.status < 500);
}

function describeHttpErrorEvent(event: any, err: HttpErrorResponse): void {
  const response: any = err.error;
  const responseMessage =
    typeof response === 'string'
      ? response
      : (response?.message || response?.error || response?.detail || response?.Message || '').toString();
  const status = err.status || 'unknown';
  const message = `HTTP ${status}: ${responseMessage || err.message || err.statusText || 'Request failed'}`;

  const firstException = event?.exception?.values?.[0];
  if (firstException) {
    firstException.type = 'HttpErrorResponse';
    firstException.value = message;
  }

  event.tags = { ...event.tags, http_status: String(status) };
  event.contexts = {
    ...event.contexts,
    http_error: {
      message: err.message,
      responseMessage,
      status: err.status,
      statusText: err.statusText,
      url: err.url || ''
    }
  };
}

function isNetworkFetchFailure(event: any): boolean {
  const exceptionValues = event?.exception?.values;

  if (!Array.isArray(exceptionValues)) {
    return false;
  }

  return exceptionValues.some(
    (value: any) => value?.type === 'TypeError' && NETWORK_FETCH_FAILURE_PATTERN.test(value?.value ?? '')
  );
}

function hasBrowserExtensionFrame(event: any): boolean {
  const exceptionValues = event?.exception?.values;

  if (!Array.isArray(exceptionValues)) {
    return false;
  }

  return exceptionValues.some((value: any) =>
    (value?.stacktrace?.frames ?? []).some((frame: any) => BROWSER_EXTENSION_URL_PATTERN.test(frame?.filename ?? ''))
  );
}

// Code run via eval / new Function / automation tools (e.g. Puppeteer page.evaluate from crawlers) has no
// script URL, so every frame is "<anonymous>". None of our bundled code produces such a stack.
function hasOnlyAnonymousFrames(event: any): boolean {
  const exceptionValues = event?.exception?.values;

  if (!Array.isArray(exceptionValues)) {
    return false;
  }

  const frames = exceptionValues.flatMap((value: any) => value?.stacktrace?.frames ?? []);
  return frames.length > 0 && frames.every((frame: any) => frame?.filename === '<anonymous>');
}

// The app is zoneless (provideZonelessChangeDetection) and doesn't ship zone.js, so a zone.js frame means the
// error came from another Angular bundle on the page, e.g. a browser extension's content script whose source
// maps hide the extension URL ("Cannot add property rejection, object is not extensible").
function hasZoneJsFrame(event: any): boolean {
  const exceptionValues = event?.exception?.values;

  if (!Array.isArray(exceptionValues)) {
    return false;
  }

  return exceptionValues.some((value: any) =>
    (value?.stacktrace?.frames ?? []).some((frame: any) => /(^|\/)zone\.js\//.test(frame?.filename ?? ''))
  );
}

function isIgnoredSentryMessage(message: string): boolean {
  return (
    message.includes(GOOGLE_MAPS_PLACES_LOAD_ERROR) ||
    message === GOOGLE_MAPS_ZERO_RESULTS_STATUS ||
    message.includes(`: ${GOOGLE_MAPS_ZERO_RESULTS_STATUS}`) ||
    message.includes(GOOGLE_MAPS_TRUSTED_TYPES_CALLBACK_ERROR) ||
    message.includes(THIRD_PARTY_SCRIPT_PARSE_ERROR)
  );
}

function getSentryEventMessages(event: any, hint: any): string[] {
  const messages = [
    event?.message,
    hint?.originalException,
    hint?.originalException?.message,
    hint?.syntheticException?.message
  ];
  const exceptionValues = event?.exception?.values;

  if (Array.isArray(exceptionValues)) {
    exceptionValues.forEach((value: any) => {
      messages.push(value?.type, value?.value);
    });
  }

  return messages
    .map((message) => (typeof message === 'string' ? message : message?.toString?.()))
    .filter((message: string | undefined): message is string => !!message);
}

function bootstrap() {
  if (environment.sentry.enabled && environment.sentry.dsn) {
    Sentry.init({
      dsn: environment.sentry.dsn,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: environment.sentry.tracesSampleRate,
      tracePropagationTargets: environment.sentry.tracePropagationTargets,
      replaysSessionSampleRate: environment.sentry.replaysSessionSampleRate,
      replaysOnErrorSampleRate: environment.sentry.replaysOnErrorSampleRate,
      beforeSend(event: any, hint: any) {
        if (shouldDropSentryEvent(event, hint)) {
          return null;
        }

        const originalException = hint?.originalException;
        if (isHttpErrorResponse(originalException)) {
          if (!shouldReportHttpError(originalException)) {
            return null;
          }
          describeHttpErrorEvent(event, originalException);
        }

        return event;
      }
    });
  }

  bootstrapApplication(AppComponent, {
    providers: createAppProviders()
  }).catch((err) => console.error(err));
}

if (document.readyState === 'complete') {
  bootstrap();
} else {
  document.addEventListener('DOMContentLoaded', bootstrap);
}
